<template>
  <CustomModal class="page-editor-modal" v-bind:id="id">
    <!-- modal header -->
    <div slot="modal-header" class="container-fluid">
      <div class="row btn-row">
        <div class="col-12">
          <div class="page-editor-header mx-auto">
            <div class="row">
              <div class="col-6">
                <h5 class="action-header typography--headline typography--color-secondary">
                  {{ $t(`page_editor.header.${pageType}`) }}
                </h5>
              </div>

              <div class="col-6">
                <button type="button"
                        class="btn btn-outline-primary float-right"
                        v-on:click="close()">
                  {{ $t('common.close') }}
                </button>

                <button type="button"
                        class="btn btn-primary float-right"
                        v-bind:disabled="addQuizDisabled"
                        v-if="pageType === 'non_quiz'"
                        v-on:click="addNonQuiz()">
                  {{ $t('common.complete') }}
                </button>

                <button type="button"
                        class="btn btn-primary float-right"
                        v-bind:disabled="addQuizDisabled"
                        v-else
                        v-on:click="addQuiz()">
                  {{ $t('common.complete') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row editor-toolbar-row">
        <div class="col-12">
          <div class="mw-620 mx-auto">
            <EditorToolbar
              @image-added="onImageAdded"
              @audio-added="onAudioAdded"
              @show-add-video-modal="showAddVideoModal"
            />
          </div>
        </div>
      </div>

      <div class="row editor-audio-row" v-if="audios.length > 0">
        <div class="col-12">
          <div class="mw-620 mx-auto relative-position">
            <LcAudio :audio="audios[0]"/>

            <div class="remove-audio-btn">
              <img
                src="/static/images/score-guide-close.svg"
                alt="remove audio"
                class="clickable"
                @click="audios.pop()"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- modal body -->
    <div class="container page-editor-body">
      <div class="row">
        <div class="col-12">
          <div class="mw-620 mx-auto">

            <!-- quiz content -->
            <div class="quiz-content-container" v-if="quiz">
              <h6 class="typography--body-1 typography--color-secondary">
                <strong v-if="pageType === 'non_quiz'">{{ $t('page_editor.non_quiz.title') }}</strong>
                <strong v-else>{{ $t('page_editor.question.title') }}</strong>
                <span class="typography--caption" v-if="!sourceEditAvaliable">(수정 불가)</span>
              </h6>
              <hr>

              <BaseEditor v-bind:editor-id="`quiz-editor-${id}`"
                          class="quiz-editor"
                          v-model="quiz.content"
                          v-bind:text-limit="5000"
                          v-bind:disabled="!sourceEditAvaliable"
                          v-on:error="checkContentError"
                          v-bind:placeholder="pageType === 'non_quiz' ? $t('page_editor.non_quiz.placeholder') : $t('page_editor.question.placeholder')">
              </BaseEditor>
            </div>


            <!-- quiz answers: multiple-choices -->
            <div class="quiz-answers-container" v-if="pageType === 'multiple_choice'">
              <div class="row">
                <div class="col-3">
                  <h6 class="typography--body-1 typography--color-secondary">
                    <strong>{{ $t('page_editor.choices.title') }}</strong>
                    <span class="typography--caption" v-if="!sourceEditAvaliable">(수정 불가)</span>
                  </h6>
                </div>

                <div class="col-9">
                  <div class="float-right">
                    <small class="typography--caption typography--color-secondary align-bottom">
                      {{ $t('page_editor.choices.hint') }}
                    </small>
                  </div>
                </div>
              </div>
              <hr>

              <div>
                <div v-for="(answer, index) in answers" v-bind:key="getAnswerKey(answer)" v-bind:style="sourceEditAvaliable ? '' : 'pointer-events: none;'">
                  <div class="quiz-answer-box" v-bind:class="{ 'has-error': answer.content && answer.content.length > 499 }">
                    <div class="delete-quiz-answer-btn clickable"
                          v-on:click="removeAnswer(index)"
                          v-show="answers.length > 2 && getAnswerKey(answer) !== getAnswerKey(chosenAnswer)">
                      <img src="/static/images/icon-delete.svg" alt="delete quiz answer choice">
                    </div>

                    <div class="quiz-choice-item-index float-left">{{ index + 1 }}.</div>

                    <div class="quiz-choice-editor">
                      <AutosizeTextarea v-model="answer.content"
                                        v-bind:placeholder="$t('page_editor.choices.placeholder')">
                      </AutosizeTextarea>
                    </div>

                    <div class="quiz-choice-item-radio-box float-right">
                      <Radio v-on:change="selectCorrectAnswerChoice(answer)"
                              v-bind:model-value="chosenAnswer"
                              v-bind:check-value="answer"
                              v-bind:id="`quiz-choice-item-radio-${id}-${index}`"
                              left-label="true">
                        <span class="typography--body-3 typography--color-secondary"
                              slot="left-label"
                              v-show="getAnswerKey(chosenAnswer) === getAnswerKey(answer)">
                          {{ $t('page_editor.choices.is_correct')}}
                        </span>
                      </Radio>
                    </div>
                  </div>
                  <div class="typography--caption typography--color-alert text-center pb-12"
                       v-if="answer.content && answer.content.length > 499">
                    {{ $t('page_editor.choices.alerts.max_length') }}
                  </div>
                </div>

                <div v-on:click="addNewAnswer()" v-show="answers.length < 5">
                  <AddAnswerButton>
                    {{ $t('page_editor.choices.add_new.placeholder') }}
                  </AddAnswerButton>
                </div>
              </div>
            </div>

            <!-- quiz answer view types: multiple-choices -->
            <div class="quiz-view-type-container" v-if="pageType === 'multiple_choice'">
              <h6 class="typography--body-1 typography--color-secondary">
                <strong>{{ $t('page_editor.choices.view_types.title') }}</strong>
              </h6>

              <div class="row" >
                <div class="col-sm-6">
                  <div class="quiz-view-type-box">
                    <div class="quiz-view-type-img text-button-type-img"></div>

                    <Radio v-on:change="selectAnswerViewType" v-bind:model-value="quiz.answer_view_type" check-value="list" id="text-button-radio">
                      <span class="typography--body-3 typography--color-secondary" slot="label">
                        {{ $t('page_editor.choices.view_types.text_button_type')}}
                      </span>
                    </Radio>
                  </div>
                </div>

                <div class="col-sm-6" >
                  <div class="quiz-view-type-box">
                    <div class="quiz-view-type-img number-button-type-img"></div>

                    <Radio v-on:change="selectAnswerViewType" v-bind:model-value="quiz.answer_view_type" check-value="control" id="number-button-radio">
                      <span class="typography--body-3 typography--color-secondary" slot="label">
                        {{ $t('page_editor.choices.view_types.number_button_type') }}
                      </span>
                    </Radio>
                  </div>
                </div>
              </div>

              <div class="typography--caption typography--color-alert text-center pt-12" v-if="hasLongAnswerChoices">
                {{ $t('page_editor.choices.alerts.view_type_hint') }}
              </div>
            </div>

            <!-- quiz answers: text-entry -->
            <div class="quiz-answers-container" v-if="pageType === 'text_entry'">
              <div class="row">
                <div class="col-3">
                  <h6 class="typography--body-1 typography--color-secondary">
                    <strong>{{ $t('page_editor.text_entry.title') }}</strong>
                    <span class="typography--caption" v-if="!sourceEditAvaliable">(수정 불가)</span>
                  </h6>
                </div>

                <div class="col-9">
                  <div class="float-right">
                    <small class="typography--caption typography--color-secondary align-bottom">
                      {{ $t('page_editor.text_entry.hint') }}
                    </small>
                  </div>
                </div>
              </div>
              <hr>

              <div v-bind:style="sourceEditAvaliable ? '' : 'pointer-events: none;'">
                <div v-for="(answer, index) in answers" v-bind:key="getAnswerKey(answer)">
                  <div class="quiz-answer-box" v-bind:class="{ 'has-error': answer.content && answer.content.length > 499 }">
                    <div class="delete-quiz-answer-btn clickable"
                          v-on:click="removeAnswer(index)"
                          v-show="index > 0">
                      <img src="/static/images/icon-delete.svg" alt="delete quiz answer choice">
                    </div>

                    <div class="quiz-text-entry-editor">
                      <AutosizeTextarea v-model="answer.content"
                                        v-bind:placeholder="$t('page_editor.text_entry.placeholder')">
                      </AutosizeTextarea>
                    </div>
                    <div v-if="isPublisherUser && !answer.is_correct">오답</div>
                    <div v-if="isPublisherUser && answer.criteria">{{ `관리자 설정 (${answer.criteria})` }}</div>
                  </div>
                  <div class="typography--caption typography--color-alert text-center pb-12"
                       v-if="answer.content && answer.content.length > 499">
                    {{ $t('page_editor.choices.alerts.max_length') }}
                  </div>
                </div>

                <div v-on:click="addNewAnswer(true)" v-show="answers.length < 5">
                  <AddAnswerButton>
                    {{ $t('page_editor.text_entry.add_new.placeholder') }}
                  </AddAnswerButton>
                </div>
              </div>

              <template v-if="isPublisherUser">
                <br />
                <br />
                <div class="row">
                  <div class="col-12">
                    <h6 class="typography--body-1 typography--color-secondary">
                      <strong>정답기준</strong>
                    </h6>
                  </div>
                  <div
                    class="col-12"
                    v-for="(answerCriteria, index) in answerCriteriaTypes"
                    :key="index"
                  >
                    <Checkbox
                      @change="selectAnswerCriteria"
                      :model-value="selectedAnswerCriteriaTypes"
                      :check-value="answerCriteria.value"
                    >
                      <span class="typography--body-3 typography--color-secondary" slot="label">
                        {{ answerCriteria.label }}
                      </span>
                    </Checkbox>
                  </div>
                </div>
              </template>
            </div>


            <!-- quiz score allotment: essay-type -->
            <div class="quiz-score-allotment-container" v-if="pageType === 'essay_type'">
              <div class="row">
                <div class="col-3">
                  <h6 class="typography--body-1 typography--color-secondary">
                    <strong>{{ $t('page_editor.score_allotment.title') }}</strong>
                  </h6>
                </div>

                <div class="col-9">
                  <div class="float-right">
                    <small class="typography--caption typography--color-secondary align-bottom">
                      {{ $t('page_editor.score_allotment.hint') }}
                    </small>
                  </div>
                </div>
              </div>
              <hr>

              <div>
                <b-dropdown v-bind:text="$t('common.numeric_score', { n: quiz.score })" class="score-select-dropdown">
                  <b-dropdown-item v-for="n in 10"
                                    v-on:click="quiz.score = n"
                                    v-bind:key="n">
                    {{ $t('common.numeric_score', { n }) }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>

            <!-- quiz grade keywords: essay-type -->
            <div class="quiz-grading-keywords-container" v-if="pageType === 'essay_type'">
              <div class="row">
                <div class="col-3">
                  <h6 class="typography--body-1 typography--color-secondary">
                    <strong>{{ $t('page_editor.grading_keywords.title') }}</strong>
                    <small class="typography--color-disabled">{{ $t('common.optional') }}</small>
                  </h6>
                </div>

                <div class="col-9">
                  <div class="float-right">
                    <small class="typography--caption typography--color-secondary align-bottom">
                      {{ $t('page_editor.grading_keywords.hint') }}
                    </small>
                  </div>
                </div>
              </div>
              <hr>

              <InputTag v-bind:tags="gradingKeywords"
                        v-bind:class="{ 'no-grading-keyword': gradingKeywords.length === 0 }"
                        v-bind:placeholder="$t(gradingKeywords.length > 0 ? 'page_editor.grading_keywords.short_placeholder' : 'page_editor.grading_keywords.long_placeholder')">
              </InputTag>
            </div>


            <!-- quiz description -->
            <div class="quiz-description-container" v-if="pageType !== 'non_quiz' && quiz">
              <h6 class="typography--body-1 typography--color-secondary" v-if="pageType !== 'essay_type'">
                <strong>{{ $t('page_editor.description.title') }}</strong>
                <span class="typography--caption" v-if="!sourceEditAvaliable">(수정 불가)</span>
                <small class="typography--color-disabled">{{ $t('common.optional') }}</small>
              </h6>

              <div class="row" v-else>
                <div class="col-3">
                  <h6 class="typography--body-1 typography--color-secondary">
                    <strong>{{ $t('page_editor.description.title') }}</strong>
                    <small class="typography--color-disabled">{{ $t('common.optional') }}</small>
                  </h6>
                </div>

                <div class="col-9">
                  <div class="float-right">
                    <small class="typography--caption typography--color-secondary align-bottom">
                      {{ $t('page_editor.description.essay_type_hint') }}
                    </small>
                  </div>
                </div>
              </div>

              <hr>

              <BaseEditor v-bind:editor-id="`quiz-desc-editor-${id}`"
                          class="quiz-editor"
                          v-model="quiz.description"
                          v-bind:text-limit="5000"
                          v-on:error="checkDescriptionError"
                          v-bind:disabled="!sourceEditAvaliable"
                          v-bind:placeholder="$t('page_editor.description.placeholder')"
                          v-if="pageType !== 'essay_type'">
              </BaseEditor>

              <div class="essay-type-desc-editor" v-else>
                <AutosizeTextarea v-model="quiz.description"
                                  v-bind:limit="5000"
                                  v-bind:placeholder="$t('page_editor.description.placeholder')">
                </AutosizeTextarea>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <div slot="modal-footer"></div>

  </CustomModal>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { isEmpty, cloneDeep, get, sum, filter, map } from 'lodash';
  import InputTag from 'vue-input-tag';
  import AddAnswerButton from './AddAnswerButton';
  import { answerCriteriaTypes } from '../../../constants';

  export default {
    name: 'page-editor-modal',

    components: {
      AddAnswerButton,
      InputTag,
    },

    props: {
      id: {
        type: String,
      },
      newPageType: {
        type: String,
        default: 'multiple_choice',
      },
      page: {
        type: Object,
        default() {
          return {};
        },
      },
      isShown: {
        type: Boolean,
      },
    },

    data() {
      return {
        pageType: null,

        chosenAnswer: 0,

        quiz: null,

        answers: [],

        audios: [],

        images: [],

        gradingKeywords: [],

        errors: {
          content: false,
          description: false,
        },

        selectedAnswerCriteriaTypes: [],
      };
    },

    computed: {
      ...mapGetters([
        'currentPublisherInfo',
        'currentUser',
        'isPublisherUser',
      ]),

      sourceEditAvaliable() {
        return (this.page.source_type !== 'edubank' && this.page.source_type !== 'ebs');
      },

      hasLongAnswerChoices() {
        return this.answers.filter(answer => (
          answer.content && answer.content.length > 30
        )).length > 0;
      },

      hasAnswersError() {
        return this.answers.filter(answer => (
          answer.content && answer.content.length > 499
        )).length > 0;
      },

      hasNoCorrectAnswer() {
        if (this.pageType === 'multiple_choice' && !this.chosenAnswer) {
          return true;
        }

        if (this.pageType === 'text_entry' && this.answers.length > 0 &&
            this.answers[0].content && this.answers[0].content.length === 0) {
          return true;
        }

        return false;
      },

      addQuizDisabled() {
        return this.errors.content || this.errors.description ||
          this.hasAnswersError || this.hasNoCorrectAnswer;
      },

      videoCount() {
        const content = get(this.quiz, 'content') || '';
        const description = get(this.quiz, 'description') || '';
        const contentVideoCount = (content.match(/<iframe/g) || []).length;
        const descriptionVideoCount = (description.match(/<iframe/g) || []).length;

        return contentVideoCount + descriptionVideoCount;
      },

      answerCriteriaTypes() {
        return answerCriteriaTypes || [];
      },

      sumByAnswerCriteria() {
        if (isEmpty(this.selectedAnswerCriteriaTypes)) {
          return 0;
        }

        return sum(this.selectedAnswerCriteriaTypes);
      },
    },

    watch: {
      page() {
        if (!isEmpty(this.page)) {
          this.setEditingPage();
        }
      },

      isShown() {
        if (this.isShown) {
          this.setup();
        }
      },
    },

    methods: {
      checkContentError(hasError) {
        this.errors.content = hasError;
      },

      checkDescriptionError(hasError) {
        this.errors.description = hasError;
      },

      getAnswerKey(answer) {
        return answer.key || answer.id;
      },

      selectAnswerViewType(selectedAnswerViewType) {
        this.quiz.answer_view_type = selectedAnswerViewType;
      },

      addNewAnswer(isCorrect) {
        if (this.answers.length < 5) {
          this.answers.push({
            key: Math.floor(Math.random() * 1000),
            content: '',
            is_correct: isCorrect || false,
          });
        }
      },

      removeAnswer(index) {
        if (this.pageType === 'multiple_choice' && this.answers.length > 2) {
          this.answers.splice(index, 1);
        }

        if (this.pageType === 'text_entry' && this.answers.length > 1) {
          this.answers.splice(index, 1);
        }
      },

      selectCorrectAnswerChoice(answer) {
        this.chosenAnswer = answer;
      },

      createEmptyQuizPage() {
        switch (this.pageType) {
          case 'multiple_choice':
            return {
              author_id: this.currentUser.id,
              author_name: this.currentUser.name,
              publisher_id: this.currentPublisherInfo.id,
              publisher_name: this.currentPublisherInfo.name,
              is_copied_page: false,
              is_store_page: false,
              is_quiz: true,
              score: 1,
              answer_view_type: 'list',
              page_type_id: 2,
              image_count: 0,
              audio_count: 0,
              video_count: 0,
              content: '',
              description: '',
              share_level: 1, // TODO
              is_autograde: true,
              language: 'KR', // TODO
            };
          case 'text_entry':
            return {
              author_id: this.currentUser.id,
              author_name: this.currentUser.name,
              publisher_id: this.currentPublisherInfo.id,
              publisher_name: this.currentPublisherInfo.name,
              is_copied_page: false,
              is_store_page: false,
              is_quiz: true,
              score: 1,
              page_type_id: 3,
              image_count: 0,
              audio_count: 0,
              video_count: 0,
              content: '',
              description: '',
              share_level: 1, // TODO
              is_autograde: true,
              language: 'KR', // TODO
              answer_criteria: 0,
            };
          case 'essay_type':
            return {
              author_id: this.currentUser.id,
              author_name: this.currentUser.name,
              publisher_id: this.currentPublisherInfo.id,
              publisher_name: this.currentPublisherInfo.name,
              is_copied_page: false,
              is_store_page: false,
              is_quiz: true,
              score: 1,
              page_type_id: 4,
              image_count: 0,
              audio_count: 0,
              video_count: 0,
              content: '',
              description: '',
              share_level: 1, // TODO
              is_autograde: false,
              language: 'KR', // TODO
            };
          case 'non_quiz':
            return {
              author_id: this.currentUser.id,
              author_name: this.currentUser.name,
              publisher_id: this.currentPublisherInfo.id,
              publisher_name: this.currentPublisherInfo.name,
              is_copied_page: false,
              is_store_page: false,
              is_quiz: false,
              page_type_id: 1,
              image_count: 0,
              audio_count: 0,
              video_count: 0,
              content: '',
              description: '',
              share_level: 1, // TODO
              is_autograde: true,
              language: 'KR', // TODO
            };
          default:
            return {
              author_id: this.currentUser.id,
              author_name: this.currentUser.name,
              publisher_id: this.currentPublisherInfo.id,
              publisher_name: this.currentPublisherInfo.name,
              is_copied_page: false,
              is_store_page: false,
              is_quiz: true,
              image_count: 0,
              audio_count: 0,
              video_count: 0,
              content: '',
              description: '',
              share_level: 1, // TODO
              is_autograde: true,
              language: 'KR', // TODO
            };
        }
      },

      resetQuizPage() {
        this.quiz = this.createEmptyQuizPage();
        this.gradingKeywords = [];
        this.answers.splice(0);
        this.images = [];
        this.audios = [];
        this.chosenAnswer = 0;
        this.selectedAnswerCriteriaTypes = [];
      },

      setEditingPage() {
        switch (this.page.page_type_id) {
          case 1:
            this.pageType = 'non_quiz';
            break;
          case 2:
            this.pageType = 'multiple_choice';
            break;
          case 3:
            this.pageType = 'text_entry';
            break;
          case 4:
            this.pageType = 'essay_type';
            break;
          default:
            this.pageType = '';
            break;
        }

        this.quiz = cloneDeep(this.page);
        this.answers = cloneDeep(this.page.answers) || [];
        this.images = cloneDeep(this.page.images) || [];
        this.audios = cloneDeep(this.page.audios) || [];

        delete this.quiz.answers;
        delete this.quiz.images;
        delete this.quiz.audios;

        if (this.pageType === 'multiple_choice') {
          this.answers.forEach((answer) => {
            if (answer.is_correct) {
              this.chosenAnswer = answer;
            }
          });
        }

        if (this.pageType === 'text_entry' && this.quiz.answer_criteria !== 0) {
          this.selectedAnswerCriteriaTypes = map(filter(answerCriteriaTypes, (answerCriteria) => {
            // eslint-disable-next-line no-bitwise
            if (answerCriteria.value & this.quiz.answer_criteria) {
              return answerCriteria.value;
            }

            return false;
          }), 'value');
        }

        if (!this.quiz.description) {
          this.quiz.description = '';
        }

        if (this.pageType === 'essay_type' && this.quiz.grading_keywords) {
          this.gradingKeywords = this.quiz.grading_keywords.split(',');
        }
      },

      onImageAdded(image) {
        this.images.push(image);
      },

      onAudioAdded(audio) {
        if (this.audios.length > 0) {
          return this.$root.$emit('show::modal', 'max-audio-limit-alert');
        }

        if (this.videoCount > 0) {
          return this.$root.$emit('show::modal', 'only-audio-or-video-alert');
        }

        return this.audios.push(audio);
      },

      showAddVideoModal() {
        if (this.audios.length > 0) {
          return this.$root.$emit('show::modal', 'only-audio-or-video-alert');
        }

        return this.$root.$emit('show::modal', 'add-video-modal');
      },

      addNonQuiz() {
        const images = this.images.filter(img => (
          this.quiz.content.indexOf(`data-image-id="${img.id}"`) !== -1 ||
          this.quiz.description.indexOf(`data-image-id="${img.id}"`) !== -1 ||
          this.quiz.content.indexOf(`data-image-id="${img.key}"`) !== -1 ||
          this.quiz.description.indexOf(`data-image-id="${img.key}"`) !== -1
        ));

        const newNonQuizPage = {
          ...this.quiz,

          images,
          audios: [...this.audios],
          image_count: images.length,
          audio_count: this.audios.length,
          video_count: this.videoCount,
        };

        newNonQuizPage.content = newNonQuizPage.content.replace(/class="mce-preview-object mce-object-iframe"/g, '');
        newNonQuizPage.description = newNonQuizPage.description.replace(/class="mce-preview-object mce-object-iframe"/g, '');

        this.$emit('page-added', newNonQuizPage);
        this.clean();
      },

      addQuiz() {
        const images = this.images.filter(img => (
          this.quiz.content.indexOf(`data-image-id="${img.id}"`) !== -1 ||
          this.quiz.description.indexOf(`data-image-id="${img.id}"`) !== -1 ||
          this.quiz.content.indexOf(`data-image-id="${img.key}"`) !== -1 ||
          this.quiz.description.indexOf(`data-image-id="${img.key}"`) !== -1
        ));

        const answers = this.answers.map(answer => ({
          ...answer,
          is_correct: (
            (this.pageType === 'text_entry' && answer.is_correct)
            || this.getAnswerKey(answer) === this.getAnswerKey(this.chosenAnswer)
          ),
        }))
        .filter((answer) => {
          if (this.pageType === 'multiple_choice') {
            return true;
          }

          if (this.pageType === 'text_entry' && answer.content === '') {
            return false;
          }

          return true;
        });

        const newQuizPage = {
          ...this.quiz,

          answers,
          images,
          audios: [...this.audios],
          image_count: images.length,
          audio_count: this.audios.length,
          video_count: this.videoCount,
          answer_criteria: this.sumByAnswerCriteria,
        };

        newQuizPage.content = newQuizPage.content.replace(/class="mce-preview-object mce-object-iframe"/g, '');
        newQuizPage.description = newQuizPage.description.replace(/class="mce-preview-object mce-object-iframe"/g, '');

        if (this.pageType === 'essay_type') {
          newQuizPage.grading_keywords = this.gradingKeywords.join(',');
        }

        this.$emit('page-added', newQuizPage);
        this.clean();
      },

      setup() {
        if (isEmpty(this.page)) {
          this.pageType = this.newPageType;

          if (!this.quiz) {
            this.quiz = this.createEmptyQuizPage();
          }

          if (this.answers.length === 0) {
            if (this.pageType === 'multiple_choice') {
              this.addNewAnswer();
              this.addNewAnswer();
              this.selectCorrectAnswerChoice(this.answers[0]);
            } else {
              this.addNewAnswer(true);
            }
          }
        } else {
          this.setEditingPage();
        }

        tinymce.editors.forEach((editor) => {
          if (editor.id === `quiz-editor-${this.id}`) {
            tinymce.setActive(editor);
          }
        });
      },

      close() {
        this.$emit('cancel');
        this.clean();
      },

      clean() {
        this.resetQuizPage();
      },

      selectAnswerCriteria(value) {
        this.selectedAnswerCriteriaTypes = value;
      },
    },

    beforeDestroy() {
      this.clean();
    },

    created() {
      this.setup();
    },
  };
</script>

<style lang="scss">
  .page-editor-modal {
    .quiz-grading-keywords-container {
      .vue-input-tag-wrapper {
        padding: 12px 14px;
        padding-bottom: 0;
        border-radius: 4px;
        -webkit-appearance: initial;

        .input-tag {
          padding: 12px 14px;
          font-size: 20px;
          font-weight: 300;
          line-height: 1.0;
          color: #fff;
          background-color: #00c896;
          border: none;
          border-radius: 7px;
          margin-right: 12px;
          margin-bottom: 12px;
        }

        input {
          width: 120px;
          font-size: 20px;
          line-height: 1.0;
          font-weight: 400;
          padding: 10px 0;
          color: rgba(0, 0, 0, 0.87);
          margin: 0;
          margin-bottom: 12px;

          &::placeholder {
            color: rgba(0, 0, 0, 0.26);
          }
        }

        &.no-grading-keyword input {
          width: 100%;
        }

        a.remove {
          background-image: url('/static/images/ic-keyword-delete.svg');
          display: inline-block;
          width: 16px;
          height: 16px;
          margin-left: 12px;
        }

        a.remove:before {
          content: '';
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/my-cards/components/PageEditorModal.vue